import {
  computed,
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  Renderer2,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ChangeCallback, TouchedCallback } from '@fmnts/common/forms';
import * as Decimal from '@fmnts/core/decimal';
import * as Fn from 'effect/Function';
import * as O from 'effect/Option';

const _equalOptionDecimals = O.getEquivalence(Decimal.Equivalence);

/**
 * Matches a string for numeric input.
 *
 * Allows
 * - `.` or `,` as a decimal separator
 * - skip leading zero (e.g. `.45`)
 * - Leading zeros (e.g. `0123.45`)
 *
 * Does not allow
 * - Thousands separator(s)
 * - A signed number (e.g. `-123` or `+123`)
 * - to end on a decimal separator (e.g. `123,`)
 */
const regexNumericInput = /^[0-9]*[\.\,]?[0-9]+$/;

interface NumberFormatOptions {
  /** Maximum number of fraction digits */
  maxFrac: number;
}

/**
 * Creates a formatting function from the given `opts`
 *
 * @param opts Options for formatting
 * @returns
 * A function that can be called with a numeric-like string.
 * The value returned by this function will confirm to the
 * provided formatting options
 */
function numberFormatter(opts: NumberFormatOptions) {
  const { maxFrac } = opts;

  // decimal pattern with <maxFrac> decimal places
  // that allows `.` and `,` as separator
  const pattern = new RegExp(`([0-9]*[\\.|\\,][0-9]{${maxFrac}})`);

  return (value: string) => {
    // Search for decimal format and return trimmed.
    const maxFractionMatch = value.match(pattern);
    if (maxFractionMatch) {
      return maxFractionMatch[0];
    }

    return value;
  };
}

/**
 * Directive to be used on HTML `<input>` elements in combination
 * with Angulars template-driven forms. This allows for dealing with
 * JavaScripts problem of floating point precision, by taking using an
 * external library for presenting precise decimals.
 * Consider using this directive to deal with money related arithmetics.
 *
 * It is not possible to use the directive in conjunction with `type="number"`
 * This is due to some limitations by browsers when it comes to implementing
 * support for decimal separators.
 */
@Directive({
  selector: 'input[fmnts-decimal-input]:not([type=number])',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DecimalInputDirective),
      multi: true,
    },
  ],
})
export class DecimalInputDirective implements ControlValueAccessor {
  private readonly _elementRef =
    inject<ElementRef<HTMLInputElement>>(ElementRef);
  private readonly _renderer = inject(Renderer2);
  private _onChange: ChangeCallback<Decimal.Decimal | null> = () => {};
  private _onTouched: TouchedCallback = () => {};

  /** The value of the input. */
  private readonly _value = signal<O.Option<Decimal.Decimal>>(O.none(), {
    equal: _equalOptionDecimals,
  });

  /** Options for formatting. */
  private readonly _numberFormatOptions = signal<NumberFormatOptions>({
    maxFrac: 2,
  });
  private readonly _formatter = computed(() =>
    numberFormatter(this._numberFormatOptions()),
  );

  /** The value to be set on the native input. */
  private readonly _nativeInputValue = computed(() =>
    Fn.pipe(
      this._value(),
      O.map((d) => d.toString()),
      O.getOrElse(() => ''),
    ),
  );

  writeValue(value: unknown): void {
    this._value.set(Decimal.fromUnknown(value));
    this.setProperty('value', this._nativeInputValue());
  }
  registerOnChange(fn: ChangeCallback): void {
    this._onChange = fn;
  }
  registerOnTouched(fn: TouchedCallback): void {
    this._onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.setProperty('disabled', isDisabled);
  }

  @HostListener('input', ['$event']) onInput(event: Event): void {
    const newValue = (event.target as HTMLInputElement).value.replace(/\s/, '');
    this.setProperty('value', newValue);

    // Emit `null` without a "numeric-like" input
    if (!regexNumericInput.test(newValue)) {
      this._updateValue(O.none());
      return;
    }

    // Format number and replace on element if necessary
    const formatted = this._formatUserInput(newValue);
    if (formatted !== newValue) {
      this.setProperty('value', formatted);
    }

    // Users might have used a different decimal separator, so we must adjust for
    // that before coersion.
    this._updateValue(Decimal.fromUnknown(formatted.replace(',', '.')));
  }

  @HostListener('blur') onBlur(): void {
    this._onTouched();
  }

  private _updateValue(value: O.Option<Decimal.Decimal>): void {
    const before = this._value();
    this._value.set(value);
    const after = this._value();

    if (!_equalOptionDecimals(before, after)) {
      this._onChange(O.getOrElse(after, () => null));
    }
  }

  private _formatUserInput(value: string): string {
    return this._formatter()(value);
  }

  /**
   * Helper method that sets a property on a target element
   * using the current `Renderer` implementation.
   *
   * @param name Name of the property
   * @param value Value of the property
   *
   * @see https://github.com/angular/angular/blob/master/packages/forms/src/directives/control_value_accessor.ts
   */
  protected setProperty(name: string, value: unknown): void {
    this._renderer.setProperty(this._elementRef.nativeElement, name, value);
  }
}
